import type { Socket } from 'socket.io-client';
import { useDataStore } from '@/stores/dataStore';
import { useAuthStore } from '@/stores/authStore';
import { useVoiceUiStore } from '@/stores/voiceUiStore';
import { voiceMedia } from '@/lib/voiceMedia';
import { initialVoiceSelfState } from '@/lib/voiceSelfState';
import * as dmCall from '@/lib/dmCall';
import { joinChannelRoom } from '@/lib/channelRoom';
import { api } from '@/lib/api';
import { addEmoji as registryAdd, removeEmoji as registryRemove } from '@/lib/emojiRegistry';
import { clearVoiceIntent, getVoiceIntentChannel, setVoiceIntent, voiceTabId } from '@/lib/voiceIntent';
import { notifyMessageActivity } from '@/lib/notifications';
import { applyAccountNotifyPrefs, resetAccountNotifyPrefs } from '@/lib/accountNotifyPrefs';
import type { AccountNotifyPrefsState } from '@bunker/shared/notifyPrefs';
import { showRatingUpdate } from '@/lib/ratingUpdate';
import { showDmToast, showMentionToast } from '@/lib/dmToastQueue';
import { applyMuteUpdate } from '@/lib/muteSettings';
import { rosterCache } from '@/lib/rosterCache';
import { emitAppEvent } from '@/lib/appEvents';
import { toast } from '@/lib/toast';
import { applyVoiceRights, askConsentInRoom } from '@/lib/voiceRights';
import i18n from '@/i18n';
import {
  playPeerJoinSound,
  playPeerLeaveSound,
  playScreenShareSound,
  playSelfJoinSound,
  playSelfLeaveSound,
} from '@/lib/sounds';
import type { Channel, DirectChannel, Guild, GuildMemberRow, Message, VoiceUserState } from '@/types/api';
// Socket payload shapes are the cross-process contract — they live in
// @bunker/shared so the server emit side and this consumer can't drift.
import type {
  ChannelActivityPayload,
  ChannelReadPayload,
  DmActivityPayload,
  DmCallSignalPayload,
  EmojiAddPayload,
  EmojiRemovePayload,
  GuildChannelsPayload,
  PollChangedPayload,
  PresencePayload,
  RatingUpdatePayload,
  ReactionPayload,
  ReadyPayload,
  ThreadCommentPayload,
  TypingPayload,
  MeUpdatePayload,
  UserUpdatePayload,
  VoiceBroadcastPayload,
  VoiceForceDisconnectPayload,
  VoiceForceMovePayload,
  VoiceSpeakingPayload,
  VoiceTakenOverPayload,
  VoiceUserLeftPayload,
  MatchActivityPayload,
} from '@bunker/shared/socketEvents.js';
import type { ChannelReadState } from '@bunker/shared';

let bound: Socket | null = null;

/** What the desktop shell's voice-channel menu shows; it refreshes when this changes. */
function voiceChannelsKey(channels: readonly Channel[] | undefined): string {
  return (channels ?? [])
    .filter((channel) => channel.type === 'VOICE')
    .map((channel) => `${channel.id}:${channel.name ?? ''}`)
    .join('|');
}

// Presence coalescing — the server pushes `presence:update` one user at a
// time, and on reconnect a burst of N. Collect them into a pending map and
// flush once per frame so a reconnect storm causes a single roster update
// instead of N spreads.
let pendingPresence: Record<string, boolean> = {};
let presenceFlushHandle: number | null = null;

function flushPresence(): void {
  presenceFlushHandle = null;
  const updates = pendingPresence;
  pendingPresence = {};
  // Nothing queued (defensive) — skip the store call entirely.
  if (Object.keys(updates).length === 0) return;
  useDataStore.getState().setPresenceBulk(updates);
}

function queuePresence(userId: string, online: boolean): void {
  pendingPresence[userId] = online;
  if (presenceFlushHandle !== null) return;
  presenceFlushHandle = requestAnimationFrame(flushPresence);
}

/**
 * Closing or reloading the tab ends the socket the same way a network drop
 * does, and the server holds a dropped voice session for a moment in case the
 * connection comes back (voiceResume). Say so explicitly, so the room sees the
 * person go at once; a reload still rejoins from the stored intent.
 */
function leaveVoiceOnPageHide(): void {
  if (bound && getVoiceIntentChannel()) bound.emit('voice:leave');
}

/**
 * Wires every server-pushed event we know about into the data store.
 * Idempotent — calling twice on the same socket is safe.
 */
export function bindSocketEvents(socket: Socket): void {
  if (bound === socket) return;
  if (bound) unbind(bound);
  bound = socket;
  window.addEventListener('pagehide', leaveVoiceOnPageHide);
  const store = useDataStore.getState();

  // Re-join the currently-open channel room every time the socket (re)connects.
  // Otherwise, after a server restart or transient disconnect, the new socket
  // sits in user/guild rooms but misses channel-scoped pushes (messages,
  // reactions, typing) until the user clicks a different channel.
  //
  // Scope: we re-join ONLY the currently-focused channel. If the user
  // switched channels while disconnected the channel rooms they passed
  // through on the way are not re-joined — which is fine because the
  // `setChannels(...)` we ship on 'ready' replaces the channel list
  // anyway. Missed `channel:activity` events are NOT replayed: unread /
  // mention counts for channels I have read before come back with
  // `ready.readStates` (the server's stored pointers), live events only add.
  // Multi-tab focus history is out of scope.
  socket.on('connect', () => {
    const cid = useDataStore.getState().currentChannelId;
    if (!cid) return;
    joinChannelRoom(cid);
    // Re-joining the room only resumes FUTURE pushes. Anything sent while we
    // were offline was never queued, so without this refetch the open channel
    // silently missed those messages until the user navigated away and back.
    // Merge (not replace) so live pushes racing this response survive.
    void api
      .listMessages(cid, { limit: 50 })
      .then((res) => {
        // The user may have switched channels while the request was in flight.
        if (useDataStore.getState().currentChannelId === cid) {
          useDataStore.getState().mergeMessages(cid, res.messages);
        }
      })
      .catch(() => {
        /* offline again — the next connect retries */
      });
  });

  socket.on('ready', (p: ReadyPayload) => {
    // My Cispracs ID notification settings (a ceiling over this device's
    // switches); missing or `enabled: false` means off. No request of our own.
    // `user` is always sent; the guard keeps a partial test or mock payload harmless.
    applyAccountNotifyPrefs(p.user?.id ?? null, p.notifyPrefs);
    store.setGuilds(p.guilds);
    // Older servers do not send it; keep what the live events told us then.
    if (p.broadcasts) useVoiceUiStore.getState().resetOnAir(p.broadcasts);
    store.setDmChannels(p.dm ?? []);
    // After guilds + DMs: a read state applies only to a channel this client knows.
    if (p.readStates) store.hydrateReadStates(p.readStates);
    const intentChannelId = getVoiceIntentChannel();
    store.setVoiceRooms(
      p.voiceRooms ?? {},
      intentChannelId ?? p.myVoiceChannelId ?? null,
    );
    store.setMatchActivities(p.matchActivities ?? {});
    if (intentChannelId) {
      // Still holding the call's media from before the drop? Ask the server
      // to keep it for this socket instead of starting over.
      const resume = voiceMedia.beginVoiceResume();
      socket.timeout(10_000).emit(
        'voice:join',
        { channelId: intentChannelId, ...initialVoiceSelfState(), tab: voiceTabId(), ...(resume ? { resume: true } : {}) },
        (err: Error | null, result?: { ok?: boolean; resumed?: boolean }) => {
          if (err || !result?.ok) {
            clearVoiceIntent();
            voiceMedia.leave();
            useDataStore.getState().setVoiceRooms(p.voiceRooms ?? {}, p.myVoiceChannelId ?? null);
            return;
          }
          voiceMedia.finishVoiceResume(result.resumed === true);
        },
      );
    }
  });

  socket.on('guild:create', (g: Guild) => store.upsertGuild(g));
  socket.on('match:activity', (p: MatchActivityPayload) => store.applyMatchActivity(p));
  // After a role edit the event carries only the id: roles live in the
  // roster detail, so that one reloads. A name/icon edit merges.
  socket.on('guild:update', (g: Guild) => {
    store.upsertGuild(g);
    rosterCache.reload(g.id);
  });
  socket.on('guild:delete', (p: { id: string }) => store.removeGuild(p.id));
  // Emitted after POST /api/me/guilds/reorder so other tabs sync the new
  // server-rail ordering without a page reload.
  socket.on('guild:reorder', (p: { order: string[] }) => store.reorderGuilds(p.order));
  // Roster events patch the cached roster in place — no refetch per change.
  socket.on('member:join', (p: GuildMemberRow) => rosterCache.memberJoined(p));
  socket.on('member:update', (p: GuildMemberRow) => rosterCache.memberUpdated(p));
  // member:leave fires for everyone in the guild, including the leaver.
  // If the leaver IS me, drop the guild from my list (server kicked me out
  // from another tab, or the owner kicked me).
  socket.on('member:leave', (p: { guildId: string; userId: string }) => {
    rosterCache.memberLeft(p.guildId, p.userId);
    const myId = useAuthStore.getState().user?.id;
    if (myId && p.userId === myId) {
      useDataStore.getState().removeGuild(p.guildId);
    }
  });
  // A member's public profile. For my own row the status is left alone: peers
  // are sent INVISIBLE as OFFLINE, and my own status arrives with me:update.
  socket.on('user:update', (p: UserUpdatePayload) => {
    const myId = useAuthStore.getState().user?.id;
    if (p.user.id === myId) {
      const { status: _publicStatus, ...profile } = p.user;
      rosterCache.userUpdated(p.guildId, profile);
    } else {
      rosterCache.userUpdated(p.guildId, p.user);
    }
    store.updateCachedUser(p.user);
  });
  // My full profile, sent to my own devices only (another tab or device of
  // mine saved it).
  socket.on('me:update', (p: MeUpdatePayload) => {
    const me = useAuthStore.getState().user;
    if (!me || me.id !== p.user.id) return;
    useAuthStore.getState().setUser({ ...me, ...p.user });
    store.updateCachedUser(p.user);
    for (const guildId of Object.keys(useDataStore.getState().guilds)) rosterCache.userUpdated(guildId, p.user);
  });

  /*
    Турнирная комната появилась, переименовалась или перестала быть турнирной.

    Худу трансляции это важно сразу: режиссёр создаёт комнату и тут же идёт
    выбирать её в панели, а оболочка перечитывает список раз в минуту — то есть
    он видит пустой выпадающий список и решает, что опять сломалось. В браузере
    моста нет, и событие просто пролетает мимо.
  */
  // The channels I can see in a server, whole: after a channel was created,
  // edited, moved or re-permissioned, or my roles changed.
  socket.on('guild:channels', (p: GuildChannelsPayload) => {
    const before = voiceChannelsKey(useDataStore.getState().guilds[p.guildId]?.channels);
    store.setGuildChannels(p.guildId, p.channels, p.myPermissions);
    if (voiceChannelsKey(p.channels) !== before) window.bunkerDesktop?.voiceChannelsChanged?.();
  });

  // The Cispracs rating was published and moved this player (Recon → Bunker).
  socket.on('rating:update', (p: RatingUpdatePayload) => showRatingUpdate(p));
  // A notification setting changed on another tab or device.
  socket.on('mute:update', applyMuteUpdate);
  // A follow-up reminder ("Ответить позже") came due — nudge with a toast that
  // opens the inbox. (The poller already cleared its remindAt; the item waits
  // in "Напоминания".)
  socket.on('followup:due', () => {
    toast.info(i18n.t('reminders.dueToast'), {
      action: { label: i18n.t('reminders.openAction'), onClick: () => emitAppEvent('reminders:open') },
    });
  });
  socket.on('channel:delete', (p: { id: string; guildId: string }) =>
    store.removeChannel(p.guildId, p.id),
  );
  socket.on('me:notify-prefs', (p: AccountNotifyPrefsState) =>
    applyAccountNotifyPrefs(useAuthStore.getState().user?.id ?? null, p),
  );
  socket.on('dm:upsert', (c: DirectChannel) => store.upsertDmChannel(c));
  socket.on('dm:team', (p) => store.updateDmTeam(p.channelId, p.userId, p.teamTag));
  socket.on('dm:delete', (p: { id: string }) => store.removeDmChannel(p.id));
  socket.on('dm:activity', (p: DmActivityPayload) => {
    const state = useDataStore.getState();
    const channel = state.dmChannels[p.channelId];
    if (channel) {
      state.upsertDmChannel({ ...channel, updatedAt: new Date().toISOString() });
    } else {
      // The user removed this DM for themselves ("удалить чат" = hide). A new
      // message un-hides it server-side, so pull the list to bring the row
      // back — otherwise the badge below counted into a chat that exists
      // nowhere in the UI (permanent phantom "(1)" in the tab title).
      void api
        .listDirectMessages()
        .then((res) => useDataStore.getState().setDmChannels(res.dm))
        .catch(() => {});
    }
    const myId = useAuthStore.getState().user?.id;
    if (myId && p.authorId !== myId) {
      // Treat reply-to-me as a mention for the badge/counter too — the
      // notification path already does this (notifications.ts honours
      // replyToAuthorId under MENTIONS). Without this, a reply-to-me
      // produced a banner + sound but the unread counter stayed at 0.
      const mentioned = p.mentions.includes(myId) || p.replyToAuthorId === myId;
      state.bumpChannelUnread(p.channelId, mentioned, null, p.createdAt);
      notifyMessageActivity({ ...p, direct: true });
      // In-app "кто пишет + превью" card (DMs carry a safe text preview).
      // Self-gating: it no-ops when the tab is hidden / you're in that chat.
      showDmToast(p);
    }
  });

  socket.on('message:create', (m: Message) => {
    store.upsertMessage(m);
    // «X печатает…» otherwise lingered for the rest of its TTL after the message.
    store.clearTyping(m.channelId, m.authorId);
  });
  socket.on('channel:activity', (p: ChannelActivityPayload) => {
    // Bump unread badge for everyone NOT focused on that channel right now.
    // bumpChannelUnread is a no-op if channelId === currentChannelId.
    const myId = useAuthStore.getState().user?.id;
    if (!myId || p.authorId === myId) return;
    // The event goes to the whole server room, including members the channel
    // is hidden from: no sound, banner or badge for a channel I can't open.
    const state = useDataStore.getState();
    if (!state.guilds[p.guildId]?.channels.some((channel) => channel.id === p.channelId)) return;
    // Same reply-to-me treatment as the dm:activity handler above —
    // see comment there.
    const mentioned = p.mentions.includes(myId) || p.replyToAuthorId === myId;
    state.bumpChannelUnread(p.channelId, mentioned, p.guildId, p.createdAt);
    notifyMessageActivity({ ...p, direct: false });
    // A mention of me or a reply to me pops up like a DM (one card, lib/dmToastQueue.tsx).
    showMentionToast(p);
  });
  // Room broadcast: keeps our own poll selection (see applyRemoteMessageUpdate).
  socket.on('message:update', (m: Message) => store.applyRemoteMessageUpdate(m));
  socket.on('message:delete', (p: { id: string; channelId: string }) =>
    store.removeMessage(p.channelId, p.id),
  );
  // History cleared for everyone (Telegram "clear history") — drop the cached
  // messages so both participants' open chat empties live.
  socket.on('channel:cleared', (p: { channelId: string }) =>
    useDataStore.getState().clearChannelMessages(p.channelId),
  );

  socket.on('channel:read', (p: ChannelReadPayload) =>
    useDataStore.getState().applyReadMarker(p.channelId, p.userId, p.messageId, p.at),
  );
  // My pointer moved on another tab / device (or this one): its badges follow.
  socket.on('read:state', (p: ChannelReadState) => useDataStore.getState().applyReadState(p));

  // Thread comments: bridged to an app event so an open ThreadPanel appends the
  // new comment. The root's reply-count rides the separate message:update.
  socket.on('thread:comment', (p: ThreadCommentPayload) =>
    emitAppEvent('thread:comment', p),
  );

  // Live poll vote / close from another viewer — splice the updated tally into
  // the cached message. applyPollUpdate keeps our own myOptionIds (the
  // broadcast omits the voter's selection).
  socket.on('poll:changed', (p: PollChangedPayload) =>
    useDataStore.getState().applyPollUpdate(p.messageId, p.poll),
  );

  socket.on('reaction:add', (p: ReactionPayload) =>
    store.applyReaction(p.channelId, p.messageId, p.userId, p.emoji, 1),
  );
  socket.on('reaction:remove', (p: ReactionPayload) =>
    store.applyReaction(p.channelId, p.messageId, p.userId, p.emoji, -1),
  );

  socket.on('presence:update', (p: PresencePayload) =>
    queuePresence(p.userId, p.online),
  );

  // Friends — the server pings both parties after any request/accept/decline/
  // unfriend. Bridge to an app event so the friends React-Query hook refetches.
  socket.on('friend:changed', () => emitAppEvent('friends:changed'));

  socket.on('typing', (p: TypingPayload) => store.noteTyping(p.channelId, p.userId));

  socket.on('voice:user:joined', (s: VoiceUserState) => {
    // Audio cue gating — only chime if the joining user is in MY current
    // voice channel. Otherwise we'd hear pings for joins in every voice
    // channel of every server we're a member of.
    const state = useDataStore.getState();
    const myId = useAuthStore.getState().user?.id;
    const myChannel = state.myVoiceChannelId;
    if (myId && s.userId === myId) {
      // I just joined — ascending chime confirms connection.
      playSelfJoinSound();
    } else if (myChannel && s.channelId === myChannel) {
      // Peer joined my channel — single soft ping.
      playPeerJoinSound();
    }
    state.applyVoiceUserState(s);
    syncCurrentVoiceMedia();
  });
  socket.on('voice:user:state', (s: VoiceUserState) => {
    const store = useDataStore.getState();
    const myId = useAuthStore.getState().user?.id;
    const myChannel = store.myVoiceChannelId;
    // Chime when a PEER in MY voice channel STARTS screen-sharing (rising edge:
    // their previous state had screenOn=false). Gated to my channel so we don't
    // ping for shares in other rooms.
    if (myId && s.userId !== myId && myChannel && s.channelId === myChannel && s.screenOn) {
      const prev = store.voiceByChannel[myChannel]?.find((u) => u.userId === s.userId);
      if (prev && !prev.screenOn) playScreenShareSound();
    }
    store.applyVoiceUserState(s);
    syncCurrentVoiceMedia();
  });
  socket.on(
    'voice:user:left',
    (p: VoiceUserLeftPayload) => {
      const state = useDataStore.getState();
      const myId = useAuthStore.getState().user?.id;
      const myChannel = state.myVoiceChannelId;
      // Cue rules — symmetric with the join handler:
      //  - I just left → self-leave chime (mirrors self-join). User
      //    noticed the asymmetry: pressing Отключиться gave no audio
      //    feedback while pressing Подключиться did.
      //  - peer left a channel I'M in → peer-leave chime
      //  - else (peer leaves a channel I'm not in) → silent
      if (myId && p.userId === myId) {
        playSelfLeaveSound();
      } else if (myChannel && p.channelId === myChannel) {
        playPeerLeaveSound();
      }
      state.removeVoiceUser(p.userId, p.channelId, p.joinedAt);
      syncCurrentVoiceMedia();
    },
  );
  // A tournament room went on or off air. Announced to the whole guild, so the
  // indicator is right for someone who opens the channel while it is already
  // live — not only for whoever was looking when it started.
  socket.on('voice:broadcast', (p: VoiceBroadcastPayload) => {
    useVoiceUiStore.getState().setOnAir(p.channelId, p.onAir ? p.since ?? Date.now() : null);
  });
  /*
    Кто говорит в комнате, которая сейчас в эфире.

    Нужно не этой странице, а худу трансляции: зритель слышит голос команды и
    не знает, чей он — пять человек в миксе звучат одинаково. Своего сокета к
    Bunker у оболочки нет, а здесь подписка и сессия уже есть, поэтому
    пересказываем через мост. В браузере моста нет, и событие просто пролетает
    мимо.
  */
  socket.on('voice:speaking', (p: VoiceSpeakingPayload) => {
    window.bunkerDesktop?.reportSpeaking?.(p.steamIds);
  });
  socket.on('voice:force-disconnect', (p: VoiceForceDisconnectPayload) => {
    clearVoiceIntent();
    voiceMedia.leave();
    if (p.channelId) {
      const myId = useAuthStore.getState().user?.id;
      if (myId) useDataStore.getState().removeVoiceUser(myId, p.channelId);
    }
  });
  // The same account joined voice from another tab/device, so THIS tab is no
  // longer the voice session — release the microphone instead of quietly
  // producing into a room the server has re-anchored elsewhere.
  socket.on('voice:taken-over', (p: VoiceTakenOverPayload) => {
    clearVoiceIntent();
    voiceMedia.leave();
    const myId = useAuthStore.getState().user?.id;
    if (myId && p.channelId) {
      useDataStore.getState().removeVoiceUser(myId, p.channelId);
    }
    toast.info(i18n.t('voice.takenOver'), { duration: 7000 });
  });
  // A role or override changed what I may send in the room I sit in.
  socket.on('voice:rights', applyVoiceRights);
  // My room became a tournament room while I sat in it: answer its notice.
  socket.on('voice:broadcast-consent', askConsentInRoom);
  socket.on('voice:force-move', (p: VoiceForceMovePayload) => {
    if (!p.channelId) return;
    setVoiceIntent(p.channelId);
    voiceMedia.leave();
  });

  // DM voice-call signaling — see lib/dmCall.ts for the state machine.
  // The socket layer just forwards each server-side event to the lib's
  // onServerEvent dispatcher; lib/dmCall keeps the in-memory state +
  // notifies subscribed UI.
  socket.on('voice:dm:incoming', (p: DmCallSignalPayload) => {
    dmCall.onServerEvent('incoming', p);
  });
  socket.on('voice:dm:cancelled', (p: DmCallSignalPayload) => {
    dmCall.onServerEvent('cancelled', p);
  });
  socket.on('voice:dm:rejected', (p: DmCallSignalPayload) => {
    dmCall.onServerEvent('rejected', p);
  });
  socket.on('voice:dm:accepted', (p: DmCallSignalPayload) => {
    dmCall.onServerEvent('accepted', p);
  });
  socket.on('voice:dm:state', (p: DmCallSignalPayload) => {
    dmCall.onServerEvent('state', p);
    // Mirror the DM voice membership into the data store, same as guild
    // voice does via voice:user:joined/left. This is what sets
    // myVoiceChannelId (when the state is mine) — and VoiceAudioLayer only
    // wires up the SFU media once myVoiceChannelId points at the call's
    // channel. Without this the call "connected" with no audio.
    const store = useDataStore.getState();
    const state = p.state as VoiceUserState | null | undefined;
    if (state && typeof state === 'object' && typeof state.userId === 'string') {
      store.applyVoiceUserState(state);
      syncCurrentVoiceMedia();
    } else if (typeof p.leftUserId === 'string' && typeof p.channelId === 'string') {
      store.removeVoiceUser(p.leftUserId, p.channelId);
      syncCurrentVoiceMedia();
      const myId = useAuthStore.getState().user?.id;
      const ch = store.dmChannels[p.channelId];
      if (p.leftUserId === myId) {
        // *We* left — and not necessarily from the call UI. Hanging up via the
        // voice sidebar, switching to a server voice channel, or a moderator
        // disconnect all land here, and the call state machine has to follow or
        // the call bar keeps showing a call that ended.
        dmCall.noteSelfLeft(p.channelId);
      } else if (ch && ch.type === 'DM') {
        // The OTHER party of a 1:1 call left → end our side too; a two-person
        // call is over once one hangs up.
        dmCall.endCallOnChannel(p.channelId);
      }
    }
  });

  // Custom server emoji — patch the in-memory registry so markdown
  // renders react to add/remove without a full refetch.
  socket.on('emoji:add', (e: EmojiAddPayload) => {
    registryAdd(e);
  });
  socket.on('emoji:remove', (p: EmojiRemovePayload) => {
    registryRemove(p.id);
  });
}

function syncCurrentVoiceMedia(): void {
  const myId = useAuthStore.getState().user?.id ?? null;
  const channelId = useDataStore.getState().myVoiceChannelId;
  if (!myId || !channelId) return;
  const room = useDataStore.getState().voiceByChannel[channelId] ?? [];
  void voiceMedia.syncParticipants(channelId, myId, room).catch((err) => {
    voiceMedia.setError(
      err instanceof Error ? err.message : i18n.t('lib2.voice.syncFailed'),
    );
  });
}

function unbind(socket: Socket): void {
  window.removeEventListener('pagehide', leaveVoiceOnPageHide);
  resetAccountNotifyPrefs();
  socket.removeAllListeners('me:notify-prefs');
  socket.removeAllListeners('connect');
  socket.removeAllListeners('ready');
  socket.removeAllListeners('guild:create');
  socket.removeAllListeners('guild:update');
  socket.removeAllListeners('guild:delete');
  socket.removeAllListeners('guild:reorder');
  socket.removeAllListeners('member:join');
  socket.removeAllListeners('member:update');
  socket.removeAllListeners('member:leave');
  socket.removeAllListeners('user:update');
  socket.removeAllListeners('me:update');
  socket.removeAllListeners('guild:channels');
  socket.removeAllListeners('channel:delete');
  socket.removeAllListeners('dm:upsert');
  socket.removeAllListeners('dm:team');
  socket.removeAllListeners('dm:delete');
  socket.removeAllListeners('dm:activity');
  socket.removeAllListeners('message:create');
  socket.removeAllListeners('channel:activity');
  socket.removeAllListeners('message:update');
  socket.removeAllListeners('message:delete');
  socket.removeAllListeners('channel:cleared');
  socket.removeAllListeners('channel:read');
  socket.removeAllListeners('read:state');
  socket.removeAllListeners('thread:comment');
  socket.removeAllListeners('poll:changed');
  socket.removeAllListeners('reaction:add');
  socket.removeAllListeners('reaction:remove');
  socket.removeAllListeners('presence:update');
  // Drain any queued presence updates so they aren't lost across a rebind,
  // and drop the stale rAF handle.
  if (presenceFlushHandle !== null) {
    cancelAnimationFrame(presenceFlushHandle);
    presenceFlushHandle = null;
  }
  flushPresence();
  socket.removeAllListeners('friend:changed');
  socket.removeAllListeners('typing');
  socket.removeAllListeners('voice:broadcast');
  socket.removeAllListeners('voice:speaking');
  socket.removeAllListeners('voice:user:joined');
  socket.removeAllListeners('voice:dm:incoming');
  socket.removeAllListeners('voice:dm:cancelled');
  socket.removeAllListeners('voice:dm:rejected');
  socket.removeAllListeners('voice:dm:accepted');
  socket.removeAllListeners('voice:dm:state');
  socket.removeAllListeners('emoji:add');
  socket.removeAllListeners('emoji:remove');
  socket.removeAllListeners('voice:user:left');
  socket.removeAllListeners('followup:due');
  socket.removeAllListeners('rating:update');
  socket.removeAllListeners('mute:update');
  socket.removeAllListeners('voice:user:state');
  socket.removeAllListeners('voice:force-disconnect');
  socket.removeAllListeners('voice:force-move');
  socket.removeAllListeners('voice:rights');
  socket.removeAllListeners('voice:broadcast-consent');
  socket.removeAllListeners('voice:taken-over');
}
